// src/pages/dashboard/tabs/financeDash.tsx
import React, { useState, useEffect } from "react";
import { Statistic, Typography, Button } from "antd";
import { DollarOutlined, FileTextOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, EyeOutlined } from "@ant-design/icons";
import { Line } from "@ant-design/charts";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

// Components
import { SectionCard, DataTable, StatusTag, InlineLoading, EmptyState } from "../../../components/shared";

// API
import { fetchCompanyDashboard } from "../../../api/finance/FinanceApi";

// Types
import { CompanyDashboardStats, Payment, Invoice, PaymentStatus, InvoiceStatus } from "../../../types/finance/FinanceTypes";
import { KPIItem } from "../components/DashboardKPIStrip";

// Utils
import { useNotifications } from "../../../context/NotificationContext";

const { Text } = Typography;

// Same props shape as the other dashboard views for a uniform DashboardPage call site.
// FinanceDash reads neither startDate nor endDate — fetchCompanyDashboard() takes no
// arguments — they are here only so the call site doesn't need a special case.
interface FinanceDashboardProps {
	startDate: Date;
	endDate: Date;
	refreshToken?: number;
	onKPIChange?: (kpis: KPIItem[]) => void;
}

const FinanceDashboard: React.FC<FinanceDashboardProps> = ({ refreshToken = 0 }) => {
	const [stats, setStats] = useState<CompanyDashboardStats | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const { showError } = useNotifications();
	const navigate = useNavigate();


	useEffect(() => {
		loadDashboardStats();
	}, [refreshToken]);

	const loadDashboardStats = async () => {
		setLoading(true);
		try {
			const data = await fetchCompanyDashboard();
			setStats(data);
		} catch (error) {
			showError("Failed to load dashboard statistics");
		} finally {
			setLoading(false);
		}
	};

	const formatCurrency = (amount: number): string =>
		new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(amount);

	const recentPaymentsColumns = [
		{
			title: "Date",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 120,
			render: (date: string) => format(new Date(date), "dd MMM yyyy"),
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			width: 100,
		},
		{
			title: "Amount",
			dataIndex: "amount",
			key: "amount",
			width: 130,
			render: (amount: number) => formatCurrency(amount),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 110,
			render: (status: PaymentStatus) => <StatusTag status={status} />,
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			ellipsis: true,
		},
		{
			title: "",
			key: "actions",
			width: 48,
			render: (_: any, record: Payment) => (
				<Button
					type="text"
					size="small"
					icon={<EyeOutlined />}
					onClick={(e) => { e.stopPropagation(); navigate(`/finance/payments?id=${record.id}`); }}
				/>
			),
		},
	];

	const recentInvoicesColumns = [
		{
			title: "Invoice #",
			dataIndex: "invoiceNumber",
			key: "invoiceNumber",
			width: 140,
		},
		{
			title: "Issue Date",
			dataIndex: "issueDate",
			key: "issueDate",
			width: 120,
			render: (date: string) => format(new Date(date), "dd MMM yyyy"),
		},
		{
			title: "Due Date",
			dataIndex: "dueDate",
			key: "dueDate",
			width: 120,
			render: (date: string) => format(new Date(date), "dd MMM yyyy"),
		},
		{
			title: "Total",
			dataIndex: "total",
			key: "total",
			width: 130,
			render: (amount: number) => formatCurrency(amount),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 110,
			render: (status: InvoiceStatus) => <StatusTag status={status} />,
		},
		{
			title: "",
			key: "actions",
			width: 48,
			render: (_: any, record: Invoice) => (
				<Button
					type="text"
					size="small"
					icon={<EyeOutlined />}
					onClick={(e) => { e.stopPropagation(); navigate(`/finance/invoices?id=${record.id}`); }}
				/>
			),
		},
	];

	const revenueChartConfig = stats
		? {
				data: stats.monthlyRevenue.map((item) => ({ month: item.month, revenue: item.amount })),
				xField: "month",
				yField: "revenue",
				point: { size: 5, shape: "circle" },
				label: { style: { fill: "var(--mg-text-secondary)" } },
		  }
		: null;

	return (
		<>
			{loading ? (
				<SectionCard>
					<InlineLoading minHeight={300} message="Loading dashboard…" />
				</SectionCard>
			) : !stats ? (
				<SectionCard>
					<EmptyState
						title="No dashboard data available"
						description="Unable to load financial statistics"
						action={{ label: "Retry", onClick: loadDashboardStats }}
					/>
				</SectionCard>
			) : (
				<>
					{/* Payment Statistics */}
					<div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
						<SectionCard>
							<Statistic
								title="Total Revenue"
								value={stats.overview.payments.totalAmount}
								precision={2}
								prefix={<DollarOutlined style={{ color: "var(--mg-success)" }} />}
								valueStyle={{ color: "var(--mg-success)" }}
								formatter={(value) => formatCurrency(Number(value))}
							/>
						</SectionCard>
						<SectionCard>
							<Statistic
								title="Total Payments"
								value={stats.overview.payments.total}
								prefix={<CheckCircleOutlined style={{ color: "var(--mg-primary)" }} />}
								valueStyle={{ color: "var(--mg-primary)" }}
								suffix={<Text type="secondary" style={{ fontSize: 14 }}>payments</Text>}
							/>
						</SectionCard>
						<SectionCard>
							<Statistic
								title="Pending Payments"
								value={stats.overview.payments.pending}
								prefix={<ClockCircleOutlined style={{ color: "var(--mg-warning)" }} />}
								valueStyle={{ color: "var(--mg-warning)" }}
								suffix={<Text type="secondary" style={{ fontSize: 14 }}>pending</Text>}
							/>
						</SectionCard>
						<SectionCard>
							<Statistic
								title="Failed Payments"
								value={stats.overview.payments.failed}
								prefix={<CloseCircleOutlined style={{ color: "var(--mg-danger)" }} />}
								valueStyle={{ color: "var(--mg-danger)" }}
								suffix={<Text type="secondary" style={{ fontSize: 14 }}>failed</Text>}
							/>
						</SectionCard>
					</div>

					{/* Invoice Statistics */}
					<div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
						<SectionCard>
							<Statistic
								title="Total Invoices"
								value={stats.overview.invoices.total}
								prefix={<FileTextOutlined style={{ color: "var(--mg-primary)" }} />}
							/>
						</SectionCard>
						<SectionCard>
							<Statistic
								title="Unpaid Invoices"
								value={stats.overview.invoices.sent}
								prefix={<ExclamationCircleOutlined style={{ color: "var(--mg-warning)" }} />}
								valueStyle={{ color: "var(--mg-warning)" }}
							/>
						</SectionCard>
						<SectionCard>
							<Statistic
								title="Overdue Invoices"
								value={stats.overview.invoices.overdue}
								prefix={<CloseCircleOutlined style={{ color: "var(--mg-danger)" }} />}
								valueStyle={{ color: "var(--mg-danger)" }}
							/>
						</SectionCard>
					</div>

					{/* Revenue Chart */}
					<SectionCard title="Revenue Trend">
						{revenueChartConfig && <Line {...revenueChartConfig} />}
					</SectionCard>

					{/* Recent Activity */}
					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
						<DataTable
							title="Recent Payments"
							columns={recentPaymentsColumns}
							dataSource={stats.recentActivity?.payments || []}
							rowKey="id"
							loading={false}
							pagination={false}
							onRowClick={(r: Payment) => navigate(`/finance/payments?id=${r.id}`)}
							emptyTitle="No recent payments"
						/>
						<DataTable
							title="Recent Invoices"
							columns={recentInvoicesColumns}
							dataSource={stats.recentActivity?.invoices || []}
							rowKey="id"
							loading={false}
							pagination={false}
							onRowClick={(r: Invoice) => navigate(`/finance/invoices?id=${r.id}`)}
							emptyTitle="No recent invoices"
						/>
					</div>
				</>
			)}
		</>
	);
};

export default FinanceDashboard;
